<script setup lang="ts">
const { storeConfig } = useStore()

const { $t } = useI18n()

const props = defineProps({
  slice: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  slices: {
    type: Array,
    required: true,
  },
})

interface KiyohReviewContentItem {
  order: number
  questionGroup: string
  questionTranslation: string
  questionType: string
  rating: string | null
}

interface KiyohReview {
  city?: string
  dateSince?: string
  rating?: number | string
  reviewAuthor?: string
  reviewComments?: string | null
  reviewContent?: KiyohReviewContentItem[]
  reviewId: string
  reviewLanguage?: string
  updatedSince?: string
}

const reviewStats = computed(() => storeConfig.value?.kiyohStats)

const reviewsFetched = ref(false)
const reviewsFetching = ref(false)
const reviewsError = ref(false)
const reviews = ref([])

async function getReviews() {
  const result = await useGraphqlQuery('kiyohStoreReviews', useGraphqlDefaultClient())
  if (reviewsFetching.value) return

  reviewsFetching.value = true
  reviews.value = []

  if (result.errors?.length) {
    console.error(result.errors)
    reviewsFetched.value = false
    reviewsError.value = true
  }
  if (result.data?.kiyohStoreReviews) {
    reviewsFetched.value = true
    reviews.value = result.data?.kiyohStoreReviews?.filter(review => review?.rating >= 6) || []
  }
  reviewsFetching.value = false
}

function extractContent(r: KiyohReview, group: string) {
  return r.reviewContent?.find(c => c.questionGroup === group)?.rating || ''
}

const averageRating = computed(() => {
  return reviewStats.value?.averageRating || '8.8'
})

const totalReviews = computed(() => reviewStats.value?.numberReviews || 4000)

const slice = computed(() => {
  if (!averageRating.value) {
    return props.slice
  }

  return {
    ...props.slice,
    Settings: {
      ...props.slice.Settings,
      Description: $t('Customers rate us {averageRating}/10. Based on {totalReviews} customer reviews', { averageRating: averageRating.value, totalReviews: totalReviews.value }),
    },
  }
})

onMounted(async () => {
  await getReviews()
})
</script>

<template>
  <StrapiSlice
    v-show="!reviewsError"
    class="ct-kiyoh-reviews"
    :slice="slice"
    :index="index"
  >
    <div class="kiyoh-reviews">
      <div class="kiyoh-reviews__container">
        <div
          v-if="(!reviewsFetched && !reviewsError) || reviewsFetching"
          class="kiyoh-reviews__state"
        >
          <Icon
            name="icon:loader"
            class="icon icon--loader"
          />
          {{ $t('Loading...') }}
        </div>
        <UiCarousel
          v-if="reviews?.length > 0 || !reviewsError"
          class="kiyoh-reviews__carousel"
          :class="{
            'ui-carousel--center': false,
            'ui-carousel--free-scroll': true,
            'ui-carousel--navigation': true,
            'ui-carousel--pagination': false,
            'ui-carousel--progress': false,
          }"
        >
          <template
            v-for="(review, i) in reviews"
            #[`slide-${i}`]
            :key="review"
          >
            <article class="kiyoh-reviews__item">
              <div class="kiyoh-review__content">
                <ReviewStars
                  v-if="review?.rating"
                  :rating="(review?.rating || 8) / 2"
                />
                <p class="kiyoh-review__title">
                  {{ extractContent(review, 'DEFAULT_ONELINER') || '' }}
                </p>
                <p class="kiyoh-review__excerpt">
                  {{ extractContent(review, 'DEFAULT_OPINION') || '' }}
                </p>

                <div class="kiyoh-review__bottom">
                  <span class="kiyoh-review__author">{{ review?.reviewAuthor || $t('Anonymously') }}</span>
                  <span class="kiyoh-review__date">
                    {{ ' - ' + new Date(review?.dateSince || '').toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) }}
                  </span>
                </div>
              </div>
            </article>
          </template>
        </UiCarousel>
      </div>
    </div>
  </StrapiSlice>
</template>

<style>
@import url('~layers/app/components/Slices/KiyohReviews.css');
</style>
